// The single verb registry (CLAUDE.md invariant #5). Each verb is declared once
// here; CLI + agent tool + reference are generated from these specs. Phase 1
// ships `watch` (the vertical slice); later phases append entries.

import { makeRecord } from "../record.js";
import { runWatch } from "../providers/tinycloud/watch.js";
import { isCustomBinding, runBoundProvider } from "../providers/run.js";
import { providerBinding } from "../providers/bindings.js";
import { providerEnv } from "../providers/provider-env.js";
import { listenVerb, seeVerb, enhanceVerb, viewVerb } from "../verbs/senses.js";
import { reconstructVerb } from "../verbs/reconstruct.js";
import { exifVerb, verifyVerb } from "../verbs/forensics.js";
import { screenshotVerb } from "../verbs/screenshot.js";
import { faceVerb } from "../verbs/face.js";
import { imageVerb } from "../verbs/image.js";
import { audioVerb } from "../verbs/audio.js";
import { voiceVerb } from "../verbs/voice.js";
import { clusterVerb } from "../verbs/cluster.js";
import { similarVerb } from "../verbs/similar.js";
import { cropVerb } from "../verbs/crop.js";
import { chronolocateVerb } from "../verbs/chronolocate.js";
import { gridVerb } from "../verbs/grid.js";
import { wallVerb } from "../verbs/wall.js";
import { situationVerb } from "../verbs/situation.js";
import { mapVerb } from "../verbs/map.js";
import { devicesVerb } from "../verbs/devices.js";
import { graphVerb } from "../verbs/graph.js";
import { resolveVideoArg } from "../verbs/media-ref.js";
import { provenanceFromCapture, stampProvenance } from "../verbs/provenance.js";
import { provenanceCase, stampArchive } from "../archive.js";
import {
  scanVerb,
  captureVerb,
  monitorVerb,
  targetVerb,
  sourceVerb,
  prebriefVerb,
} from "../verbs/osint.js";
import { indexVerb } from "../verbs/index.js";
import { archiveVerb } from "../verbs/archive.js";
import { askVerb, briefVerb } from "../verbs/read.js";
import { caseVerb } from "../verbs/case.js";
import { noteVerb } from "../verbs/note.js";
import { findingVerb } from "../verbs/finding.js";
import { setupVerb, providerVerb, doctorVerb } from "../verbs/setup.js";
import { skillsVerb } from "../verbs/skills.js";
import type { VerbSpec } from "./types.js";

export const watchVerb: VerbSpec = {
  name: "watch",
  group: "sense",
  summary: "Analyze a video into a reusable, time-anchored record (content/transcript/detailed).",
  description:
    "Runs the bound sense provider (default: tinycloud, exec) over a video file or URL " +
    "and emits a video.analysis record with markdown content, a transcript (when speech " +
    "is present), and the full structured describe in `detailed`.",
  args: [{ name: "input", summary: "Video file path or URL", required: true }],
  flags: [
    { name: "format", summary: "Output surface: json | md | txt", type: "string", choices: ["json", "md", "txt"] },
    { name: "json", summary: "Shorthand for --format json", type: "boolean" },
  ],
  outputKind: "video.analysis",
  providerKey: "watch",
  run: async (ctx) => {
    if (!ctx.input) {
      return [
        makeRecord({
          verb: "watch",
          format: "json",
          payload: { content: "", transcript: "", detailed: null },
          error: "watch requires a video input (path or URL)",
          state: "error",
        }),
      ];
    }
    const resolved = resolveVideoArg(ctx.case, ctx.input, "watch input", { requireReady: false, home: ctx.home });
    if (resolved.error) {
      return [
        makeRecord({
          verb: "watch",
          format: "json",
          payload: { content: "", transcript: "", detailed: null },
          media: { ref: ctx.input },
          error: resolved.error,
          state: "error",
        }),
      ];
    }
    const input = resolved.ref ?? ctx.input;
    // resolve the run template from the active profile binding (else default).
    const binding = providerBinding(ctx, "watch");
    // A custom provider already emits a record → dispatch by transport. Only the
    // tinycloud default needs envelope→record mapping.
    const rec = isCustomBinding(binding)
      ? await runBoundProvider("watch", binding!, input, { env: providerEnv(ctx.case.mediaDir), timeoutMs: 15 * 60_000, signal: ctx.signal, home: ctx.home })
      : await runWatch(input, { run: binding?.run, signal: ctx.signal });
    rec.meta = { ...rec.meta, case: ctx.case.dir };
    // trace back to the originating post (like listen) — for archived media the
    // capture that materialized it lives in the BUCKET, so look there
    stampProvenance(rec, provenanceFromCapture(provenanceCase(ctx.case, resolved.archive, ctx.home), input));
    // in-place sensing of an archived clip traces to its bucket, like the
    // scoped match verbs and capture pulls
    return [stampArchive(rec, resolved.archive)];
  },
};

/** The full verb registry. Append new verbs here. */
export const VERBS: VerbSpec[] = [
  watchVerb,
  listenVerb,
  seeVerb,
  faceVerb,
  imageVerb,
  audioVerb,
  voiceVerb,
  clusterVerb,
  similarVerb,
  exifVerb,
  verifyVerb,
  screenshotVerb,
  enhanceVerb,
  reconstructVerb,
  viewVerb,
  cropVerb,
  chronolocateVerb,
  gridVerb,
  wallVerb,
  situationVerb,
  mapVerb,
  devicesVerb,
  graphVerb,
  scanVerb,
  captureVerb,
  monitorVerb,
  indexVerb,
  archiveVerb,
  targetVerb,
  sourceVerb,
  noteVerb,
  findingVerb,
  prebriefVerb,
  askVerb,
  briefVerb,
  caseVerb,
  setupVerb,
  providerVerb,
  doctorVerb,
  skillsVerb,
];

export function findVerb(name: string): VerbSpec | undefined {
  return VERBS.find((v) => v.name === name);
}
